/**
 * "Transcribe all" (OutlierOS workbench, docs/legacy-ui/02 §1 D–E): estimate dialog (To transcribe / Already done /
 * No video yet, credits box) then a live progress card. Highest Wise Score first, packed into the credits left
 * (packages/core/src/transcribe-plan.ts). No more than the plan's in-flight limit is queued at once; failures refund.
 * Optionally exports afterwards ("Transcribe & export").
 */
import { useEffect, useRef, useState } from 'react';
import { Check, Loader2, Mic, X } from 'lucide-react';
import { TRANSCRIBE_MAX_PER_REQUEST } from '@vw/core/src/limits';
import { planTranscribeAll, type TranscribePlan } from '@vw/core/src/transcribe-plan';
import { busyQueueMessage, type QueueInfo } from '@vw/core/src/transcription-queue';
import { Button, Modal, toast } from '@vw/ui';
import { api, ApiError } from '../../api';
import { Link } from '../../router';
import { date } from '../../format';
import type { InspectReel } from './types';

export interface BatchState {
  total: number;
  done: number;
  failed: number;
  running: boolean;
  stopReason: string | null;
  exportLabel: string | null;
  /** The follow-up export (Transcribe & export) has downloaded. */
  exported?: boolean;
  /** Decision 84: the busy queue's ETA for the reels still waiting (minutes), while there is one. */
  queueEta?: number | null;
}

interface Credits { allowance: number; used: number; available: number; secondsPerCredit: number; resetsAt?: number }

export function TranscribeAllDialog({ open, onClose, reels, handle, exportLabel, onStart, onExportOnly, secondsPerCredit }: {
  open: boolean; onClose: () => void; reels: InspectReel[]; handle: string; exportLabel: string | null; secondsPerCredit: number;
  onStart: (plan: TranscribePlan) => void; onExportOnly: () => void;
}) {
  const [credits, setCredits] = useState<Credits | null | 'error'>(null);
  useEffect(() => {
    if (!open) return;
    setCredits(null);
    api<Credits>('/api/credits').then(setCredits, () => setCredits('error'));
  }, [open]);
  const loaded = credits && credits !== 'error' ? credits : null;
  const plan = planTranscribeAll(
    reels.map((r) => ({ pk: r.pk, outlierScore: r.outlierScore, durationS: r.durationS, hasTranscript: r.hasTranscript, hasVideo: r.transcribable ?? !!r.videoUrl })),
    { available: loaded ? loaded.available : null, secondsPerCredit: loaded?.secondsPerCredit ?? secondsPerCredit },
  );
  const nothing = plan.run.length === 0;
  const tile = (label: string, value: number) => (
    <div className="rounded-xl bg-secondary/70 p-3 text-center"><div className="text-xl font-black tabular-nums">{value}</div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div></div>
  );
  return (
    <Modal open={open} onClose={onClose} labelledBy="tx-title" title={<span className="flex items-center gap-2"><Mic className="h-4 w-4 text-primary" />{exportLabel ? 'Transcribe & export' : 'Transcribe all reels'}</span>}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        {exportLabel && nothing
          ? <Button onClick={() => { onClose(); onExportOnly(); }}>Export without new transcripts</Button>
          : <Button disabled={!loaded || nothing} onClick={() => { onClose(); onStart(plan); }}>{nothing ? 'Nothing to transcribe' : `Transcribe ${plan.run.length}${exportLabel ? ' & export' : ''}`}</Button>}
      </>}>
      <p className="-mt-2 mb-4 text-sm text-muted-foreground">Transcripts for @{handle}'s reels in this view, highest Wise Score first.</p>
      <div className="grid grid-cols-3 gap-2">{tile('To transcribe', plan.run.length + plan.skippedForCredits)}{tile('Already done', plan.alreadyDone)}{tile('No video yet', plan.noVideo)}</div>
      <div className="mt-4 space-y-2 rounded-2xl border border-border p-4 text-sm">
        {credits === null ? <p className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Checking your transcription credits…</p> : (
          <>
            <Row k="Estimated cost" v={`~${plan.estimatedCredits} credit${plan.estimatedCredits === 1 ? '' : 's'}`} />
            {plan.alreadyDone > 0 && <Row k="Already transcribed" v={<span className="text-emerald-600 dark:text-emerald-400">{plan.alreadyDone} (0 credits to open)</span>} />}
            <Row k="Left this period" v={loaded ? `${loaded.available} of ${loaded.allowance} credits${loaded.resetsAt ? ` (resets on ${date(loaded.resetsAt)})` : ''}` : 'Unknown'} />
            <Row k="Will run now" v={<span className="font-bold text-primary">{plan.run.length} reel{plan.run.length === 1 ? '' : 's'}</span>} />
            {plan.skippedForCredits > 0 && <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">{plan.skippedForCredits} reel{plan.skippedForCredits === 1 ? " won't" : "s won't"} fit in your remaining credits. <Link to="/billing" className="font-semibold underline">Upgrade</Link> for more.</p>}
            <p className="text-xs text-muted-foreground">Longer reels use more credits; the final cost uses the real length. Failed transcriptions are refunded.</p>
            {plan.noVideo > 0 && <p className="text-xs text-muted-foreground">Reels without a saved video are skipped. A scan with the extension saves the video of every reel it reads; transcribe others from the extension's side panel.</p>}
          </>
        )}
      </div>
    </Modal>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return <div className="flex items-center justify-between gap-3"><span className="text-muted-foreground">{k}</span><span className="font-semibold tabular-nums">{v}</span></div>;
}

interface JobRow { id: string; reel_pk: string; status: string; error_code: string | null; queue?: QueueInfo }
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
/** Give up waiting after this long; jobs still running finish in the background. */
const DEADLINE_MS = 30 * 60_000;

/**
 * Runs a plan. Keeps at most the plan's in-flight limit queued (the server answers "busy" past it), refills as
 * jobs finish, re-queues "busy" reels, retries 429/503 once, polls job status (joined jobs included).
 * Resolves with the reels that now have a transcript ("no speech" reels finish without one).
 * Cancel stops queuing at once and stops waiting; already queued reels finish in the background.
 */
export function useBatchRunner(onReelDone: (pks: string[]) => void) {
  const [state, setState] = useState<BatchState | null>(null);
  const cancelled = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; cancelled.current = true; }; }, []);
  const doneCb = useRef(onReelDone);
  doneCb.current = onReelDone;

  async function run(plan: TranscribePlan, exportLabel: string | null): Promise<string[]> {
    cancelled.current = false;
    const todo = plan.run.map((r) => r.pk);
    const total = todo.length;
    let done = 0;
    let failed = 0;
    let stop: string | null = null;
    const finished: string[] = [];
    const pending = new Map<string, string>(); // jobId → reelPk
    const deadline = Date.now() + DEADLINE_MS;
    // One batch for every request of this run (decision 84): bulk (released after single reels, round-robin with other
    // users) and one notification when it is all done.
    const batchId = crypto.randomUUID();
    let queueEta: number | null = null;
    const update = (running: boolean) => mounted.current && setState({ total, done, failed, running, stopReason: stop, exportLabel, queueEta: running ? queueEta : null });
    let limit = 2;
    try { limit = Math.max(1, (await api<{ inFlightLimit?: number }>('/api/credits')).inFlightLimit ?? 2); } catch { /* keep 2 */ }
    update(true);

    const settle = (pk: string, ok: boolean) => {
      if (ok) { done++; finished.push(pk); } else failed++;
    };

    while ((todo.length || pending.size) && !(cancelled.current || Date.now() > deadline)) {
      // Refill up to the in-flight limit.
      const room = Math.min(TRANSCRIBE_MAX_PER_REQUEST, limit - pending.size);
      if (room > 0 && todo.length && !stop) {
        const batch = todo.splice(0, room);
        let res: { items: Array<{ reelPk: string; status: string; jobId?: string }>; credits?: { resetsAt?: number }; queue?: QueueInfo } | null = null;
        try {
          res = await api('/api/transcribe', { method: 'POST', json: { reelPks: batch, bulk: true, batchId, batchTotal: total } });
          if (res?.queue) queueEta = Math.max(queueEta ?? 0, res.queue.etaMinutes);
        } catch (e) {
          // Too many requests: wait and retry. Paused (503) stops with the server's message instead of looping.
          if (e instanceof ApiError && e.status === 429) { todo.unshift(...batch); await sleep(5000); continue; }
          stop = (e as Error).message;
          failed += batch.length + todo.length;
          todo.length = 0;
        }
        const instant: string[] = [];
        let busy: string[] = [];
        for (const it of res?.items ?? []) {
          if (it.status === 'queued' && it.jobId) pending.set(it.jobId, it.reelPk);
          else if (it.status === 'done') { settle(it.reelPk, true); instant.push(it.reelPk); }
          else if (it.status === 'busy') busy.push(it.reelPk);
          else if (it.status === 'no_credits') {
            stop = res?.credits?.resetsAt ? `Out of transcription credits until ${date(res.credits.resetsAt)}` : 'Out of transcription credits for this billing period';
            failed += 1 + todo.length;
            todo.length = 0;
          } else failed++;
        }
        if (stop) { failed += busy.length; busy = []; }
        if (busy.length) {
          todo.unshift(...busy);
          // Everything in flight belongs to other tabs/accounts' jobs: wait before asking again.
          if (!pending.size) await sleep(3000);
        }
        if (instant.length) doneCb.current(instant);
        update(true);
        if (pending.size < limit && todo.length && !busy.length && !stop) continue;
      }
      if (!pending.size) { if (stop) break; continue; }
      await sleep(3000);
      if (cancelled.current) break;
      try {
        const ids = [...pending.keys()].slice(0, 50);
        const { jobs } = await api<{ jobs: JobRow[] }>(`/api/transcribe/jobs?ids=${ids.join(',')}`);
        const justDone: string[] = [];
        // The latest ETA of the reels still queued (null once none is waiting in the busy queue).
        const etas = jobs.filter((j) => j.status === 'queued' && j.queue).map((j) => j.queue!.etaMinutes);
        queueEta = etas.length ? Math.max(...etas) : null;
        for (const j of jobs) {
          const pk = pending.get(j.id);
          if (!pk) continue;
          if (j.status === 'done') {
            pending.delete(j.id);
            // "no speech" finishes without a transcript: counts as done, but there is nothing to show.
            if (j.error_code === 'no_speech') done++;
            else { settle(pk, true); justDone.push(pk); }
          } else if (j.status === 'failed') { pending.delete(j.id); failed++; }
        }
        if (justDone.length) doneCb.current(justDone);
      } catch { /* keep polling */ }
      update(true);
    }
    if (cancelled.current || Date.now() > deadline) {
      const left = pending.size;
      if (!stop) stop = cancelled.current
        ? `Cancelled.${left ? ` ${left} reel${left === 1 ? ' is' : 's are'} still finishing in the background.` : ''}`
        : `Still waiting on ${left} reel${left === 1 ? '' : 's'}; they finish in the background.`;
    }
    update(false);
    if (!stop && mounted.current) toast.success(`Transcribed ${done} of ${total} reel${total === 1 ? '' : 's'}`);
    return finished;
  }

  return {
    state, run,
    cancel: () => { cancelled.current = true; },
    dismiss: () => setState(null),
    /** Called once the follow-up export has downloaded. */
    exported: () => setState((s) => s && { ...s, exported: true }),
  };
}

export function BatchProgress({ s, onCancel, onDismiss }: { s: BatchState; onCancel: () => void; onDismiss: () => void }) {
  const pct = s.total ? Math.round(((s.done + s.failed) / s.total) * 100) : 100;
  return (
    <section className="space-y-2 rounded-2xl border border-border bg-card p-4 shadow-xs" aria-live="polite">
      <div className="flex items-center gap-2 text-sm">
        {s.running ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <Check className="h-4 w-4 text-emerald-500" />}
        <span className="font-semibold">{s.running ? `Transcribing reels… ${s.done + s.failed} of ${s.total}` : `Transcribed ${s.done} of ${s.total} reel${s.total === 1 ? '' : 's'}${s.exportLabel && s.exported ? ' · export downloaded' : ''}`}</span>
        {s.failed > 0 && <span className="text-xs text-rose-500">· {s.failed} failed</span>}
        <span className="ml-auto">{s.running ? <Button size="sm" variant="secondary" onClick={onCancel}>Cancel</Button> : <button aria-label="Dismiss" onClick={onDismiss} className="rounded-lg p-1 text-muted-foreground hover:bg-secondary"><X className="h-4 w-4" /></button>}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} /></div>
      {s.running && s.queueEta ? <p className="text-xs text-muted-foreground">{busyQueueMessage(s.queueEta, Math.max(2, s.total - s.done - s.failed))}</p> : null}
      {s.stopReason && <p className="text-xs text-muted-foreground">Stopped: {s.stopReason}{/credit|limit|upgrade/i.test(s.stopReason) && <> · <Link to="/billing" className="font-semibold text-primary">Upgrade plan →</Link></>}</p>}
    </section>
  );
}
